import { georgeStats, trackEvent2 } from '@shared/statistics';
import { useNotifications } from '@shared/snackbar';
import { t } from '@luna/i18n';
import { CustomizationService } from '@api-client/products';

import type { ActivePAPIProductWithAvatarData } from '../../types';
import { useCustomizationSuccessHandler } from './useCustomizationSuccessHandler';
import { getCustomizationWithFallback } from '../../services/fallbackCustomizationValueService';
import { useActivePAPIProduct } from '../../../../ActivePAPIProductContext';

const SHOW_ON_OVERVIEW_TRACKING = 'overview_show';
const HIDE_ON_OVERVIEW_TRACKING = 'overview_hide';

const SHOW_ON_OVERVIEW = false;
const HIDE_ON_OVERVIEW = true;

type OverviewAppearanceChangeHook = () => {
    showAccountOnOverview: () => void;
    hideAccountOnOverview: () => void;
};

export const useDashboardAppearanceChange: OverviewAppearanceChangeHook = () => {
    const handleSuccessfulCustomization = useCustomizationSuccessHandler();
    const {
        id,
        localizedName,
        _embedded: { customization },
    } = useActivePAPIProduct<ActivePAPIProductWithAvatarData>();
    const { displaySuccess } = useNotifications('product-settings-details-dashboard-appearance');
    const updateDashboardAppearance = async (hiddenOnOverview: boolean): Promise<void> => {
        CustomizationService.putPublicProductsCustomizationHiddenOnOverview({
            id,
            requestBody: { hiddenOnOverview },
        }).then((customizationResponse) => {
            handleSuccessfulCustomization(
                getCustomizationWithFallback(
                    { ...customization, hiddenOnOverview },
                    customizationResponse,
                ),
            );
            georgeStats.trackEvent(
                'details',
                hiddenOnOverview ? HIDE_ON_OVERVIEW_TRACKING : SHOW_ON_OVERVIEW_TRACKING,
            );
            trackEvent2('productCustomisationChanges', 'productCustomised', {
                customisationChanges: ['overviewVisibility'],
                overviewState: hiddenOnOverview ? 'hidden' : 'visible',
                productType: localizedName,
            });

            const changeActionSuccessMessage = hiddenOnOverview
                ? t('products.settings.details.hiddenOnOverview')
                : t('products.settings.details.shownOnOverview');

            displaySuccess(changeActionSuccessMessage);
        });
    };
    return {
        showAccountOnOverview: () => updateDashboardAppearance(SHOW_ON_OVERVIEW),
        hideAccountOnOverview: () => updateDashboardAppearance(HIDE_ON_OVERVIEW),
    };
};
